import { RecruitmentMetrics, Requisition, Candidate, RecruiterAlert } from '../types';
import { GLOBAL_LOCATIONS, DEPARTMENTS, JOB_LEVELS, COMPANY_INFO, formatCurrency } from '../constants/enterpriseData';

export const mockRecruitmentData = {
  metrics: {
    openRequisitions: 1847, // Scale appropriate for 132K employees
    avgTimeToFill: 42, // Healthcare industry average
    pipelineHealthIndex: 78, // Realistic for large enterprise
    offerAcceptanceRate: 84, // Healthcare industry competitive
    diversityPipelinePercentage: 67, // Strong diversity focus
    candidatesInPipeline: 8945, // Large pipeline for global enterprise
    interviewsScheduled: 892, // Weekly interviews across global locations
    offersExtended: 156, // Monthly offers
    conversionRates: {
      appliedToScreen: 68, // Healthcare screening is thorough
      screenToInterview: 47, // Selective process
      interviewToOffer: 31, // Quality over quantity
      offerToHire: 82, // Strong acceptance rate
    },
  } as RecruitmentMetrics,

  requisitions: [
    {
      id: 'REQ-001',
      title: 'Senior Medical Device Engineer',
      location: 'Bad Homburg, Germany',
      department: 'Medical Device Engineering',
      roleLevel: 'Senior',
      status: 'OPEN',
      priority: 'HIGH',
      openDate: '2024-01-15',
      targetDate: '2024-03-01',
      daysOpen: 28,
      hiringManager: 'Dr. Klaus Mueller',
      recruiter: 'Maria Gonzalez',
      salaryBand: '€95k - €125k',
      candidateCount: 45,
      stageCount: {
        applied: 20,
        screening: 12,
        interview: 8,
        offer: 3,
        hired: 2,
      },
      slaStatus: 'ON_TRACK',
    },
    {
      id: 'REQ-002',
      title: 'Clinical Research Manager',
      location: 'Waltham, MA',
      department: 'Clinical Affairs & Research',
      roleLevel: 'Manager',
      status: 'OPEN',
      priority: 'HIGH',
      openDate: '2024-01-20',
      targetDate: '2024-03-15',
      daysOpen: 23,
      hiringManager: 'Jennifer Martinez',
      recruiter: 'Alex Johnson',
      salaryBand: '$110k - $145k',
      candidateCount: 32,
      stageCount: {
        applied: 15,
        screening: 8,
        interview: 5,
        offer: 2,
        hired: 2,
      },
      slaStatus: 'ON_TRACK',
    },
    {
      id: 'REQ-003',
      title: 'Principal Data Scientist - Healthcare Analytics',
      location: 'Beijing, China',
      department: 'Data Analytics & AI',
      roleLevel: 'Principal',
      status: 'OPEN',
      priority: 'HIGH',
      openDate: '2024-01-10',
      targetDate: '2024-02-28',
      daysOpen: 33,
      hiringManager: 'Dr. Wei Zhang',
      recruiter: 'Sarah Chen',
      salaryBand: '¥850k - ¥1.1M',
      candidateCount: 28,
      stageCount: {
        applied: 10,
        screening: 8,
        interview: 6,
        offer: 3,
        hired: 1,
      },
      slaStatus: 'AT_RISK',
    },
    {
      id: 'REQ-004',
      title: 'Regulatory Affairs Specialist',
      location: 'Tokyo, Japan',
      department: 'Regulatory Affairs',
      roleLevel: 'Specialist',
      status: 'OPEN',
      priority: 'MEDIUM',
      openDate: '2024-01-25',
      targetDate: '2024-04-01',
      daysOpen: 18,
      hiringManager: 'Hiroshi Tanaka',
      recruiter: 'Anna Weber',
      salaryBand: '¥7.5M - ¥9.8M',
      candidateCount: 22,
      stageCount: {
        applied: 12,
        screening: 6,
        interview: 3,
        offer: 1,
        hired: 0,
      },
      slaStatus: 'ON_TRACK',
    },
    {
      id: 'REQ-005',
      title: 'Senior Software Engineer - Digital Health',
      location: 'São Paulo, Brazil',
      department: 'Software Development',
      roleLevel: 'Senior',
      status: 'OPEN',
      priority: 'HIGH',
      openDate: '2024-01-05',
      targetDate: '2024-02-20',
      daysOpen: 38,
      hiringManager: 'Carlos Silva',
      recruiter: 'Luis Rodriguez',
      salaryBand: 'R$180k - R$240k',
      candidateCount: 18,
      stageCount: {
        applied: 8,
        screening: 5,
        interview: 3,
        offer: 2,
        hired: 0,
      },
      slaStatus: 'BREACHED',
    },
    {
      id: 'REQ-006',
      title: 'Clinical Operations Director',
      location: 'Richmond, CA',
      department: 'Clinical Operations',
      roleLevel: 'Director',
      status: 'OPEN',
      priority: 'HIGH',
      openDate: '2024-01-12',
      targetDate: '2024-03-10',
      daysOpen: 31,
      hiringManager: 'Michael Thompson',
      recruiter: 'Jessica Wang',
      salaryBand: '$175k - $225k',
      candidateCount: 24,
      stageCount: {
        applied: 12,
        screening: 8,
        interview: 3,
        offer: 1,
        hired: 0,
      },
      slaStatus: 'ON_TRACK',
    },
    {
      id: 'REQ-007',
      title: 'Quality Assurance Manager',
      location: 'Barcelona, Spain',
      department: 'Quality Assurance',
      roleLevel: 'Manager',
      status: 'OPEN',
      priority: 'MEDIUM',
      openDate: '2024-01-22',
      targetDate: '2024-03-20',
      daysOpen: 21,
      hiringManager: 'Elena Rodriguez',
      recruiter: 'Maria Santos',
      salaryBand: '€85k - €115k',
      candidateCount: 19,
      stageCount: {
        applied: 9,
        screening: 6,
        interview: 3,
        offer: 1,
        hired: 0,
      },
      slaStatus: 'ON_TRACK',
    },
    {
      id: 'REQ-008',
      title: 'Manufacturing Engineer',
      location: 'Schweinfurt, Germany',
      department: 'Production',
      roleLevel: 'Senior Specialist',
      status: 'OPEN',
      priority: 'MEDIUM',
      openDate: '2024-01-18',
      targetDate: '2024-03-25',
      daysOpen: 25,
      hiringManager: 'Thomas Schmidt',
      recruiter: 'Anna Weber',
      salaryBand: '€75k - €95k',
      candidateCount: 16,
      stageCount: {
        applied: 8,
        screening: 5,
        interview: 2,
        offer: 1,
        hired: 0,
      },
      slaStatus: 'ON_TRACK',
    },
    {
      id: 'REQ-009',
      title: 'Biomedical Data Analyst',
      location: 'Melbourne, Australia',
      department: 'Data Analytics & AI',
      roleLevel: 'Specialist',
      status: 'OPEN',
      priority: 'LOW',
      openDate: '2024-01-28',
      targetDate: '2024-04-15',
      daysOpen: 15,
      hiringManager: 'Emma Wilson',
      recruiter: 'David Kim',
      salaryBand: 'A$85k - A$105k',
      candidateCount: 14,
      stageCount: {
        applied: 7,
        screening: 4,
        interview: 2,
        offer: 1,
        hired: 0,
      },
      slaStatus: 'ON_TRACK',
    },
    {
      id: 'REQ-010',
      title: 'Compliance Officer - Healthcare',
      location: 'Dubai, UAE',
      department: 'Legal & Compliance',
      roleLevel: 'Manager',
      status: 'OPEN',
      priority: 'HIGH',
      openDate: '2024-01-08',
      targetDate: '2024-02-25',
      daysOpen: 35,
      hiringManager: 'Ahmed Hassan',
      recruiter: 'Fatima Al-Zahra',
      salaryBand: 'AED 280k - AED 350k',
      candidateCount: 21,
      stageCount: {
        applied: 11,
        screening: 7,
        interview: 2,
        offer: 1,
        hired: 0,
      },
      slaStatus: 'AT_RISK',
    },
  ] as Requisition[],

  candidates: [
    {
      id: 'CAN-001',
      name: 'Dr. Emma Wilson',
      email: 'emma.wilson@email.com',
      phone: '+49 170 1234567',
      requisitionId: 'REQ-001',
      requisitionTitle: 'Senior Medical Device Engineer',
      stage: 'INTERVIEW',
      stageAge: 3,
      source: 'LinkedIn',
      appliedDate: '2024-01-20',
      lastActivity: '2024-02-10',
      skills: ['Medical Device Design', 'FDA 510(k)', 'ISO 13485', 'Biocompatibility', 'CAD/SolidWorks'],
      location: 'Frankfurt, Germany',
      currentRole: 'Medical Device Engineer',
      yearsOfExperience: 8,
      assessmentScore: 85,
      interviewScore: 88,
      notes: 'Strong FDA regulatory experience, excellent technical skills',
    },
    {
      id: 'CAN-002',
      name: 'Dr. James Martinez',
      email: 'james.martinez@email.com',
      phone: '+1 617 555 0234',
      requisitionId: 'REQ-002',
      requisitionTitle: 'Clinical Research Manager',
      stage: 'OFFER',
      stageAge: 1,
      source: 'Employee Referral',
      appliedDate: '2024-01-22',
      lastActivity: '2024-02-12',
      skills: ['Clinical Trial Management', 'GCP', 'Regulatory Submissions', 'Team Leadership', 'Budget Management'],
      location: 'Boston, MA',
      currentRole: 'Senior Clinical Research Associate',
      yearsOfExperience: 12,
      assessmentScore: 92,
      interviewScore: 90,
      offerDetails: {
        salary: 125000,
        bonus: 18000,
        startDate: '2024-03-15',
        expiryDate: '2024-02-25',
      },
      notes: 'Excellent clinical operations background, strong cultural fit',
    },
    {
      id: 'CAN-003',
      name: 'Li Wei',
      email: 'li.wei@email.com',
      phone: '+86 138 0013 8000',
      requisitionId: 'REQ-003',
      requisitionTitle: 'Principal Data Scientist - Healthcare Analytics',
      stage: 'INTERVIEW',
      stageAge: 2,
      source: 'University Partnership',
      appliedDate: '2024-01-15',
      lastActivity: '2024-02-11',
      skills: ['Machine Learning', 'Healthcare Analytics', 'Python', 'TensorFlow', 'Clinical Data Mining'],
      location: 'Shanghai, China',
      currentRole: 'Senior Data Scientist',
      yearsOfExperience: 10,
      assessmentScore: 95,
      interviewScore: 91,
      notes: 'Outstanding technical expertise in healthcare AI applications',
    },
    {
      id: 'CAN-004',
      name: 'Yuki Tanaka',
      email: 'yuki.tanaka@email.com',
      phone: '+81 90 1234 5678',
      requisitionId: 'REQ-004',
      requisitionTitle: 'Regulatory Affairs Specialist',
      stage: 'SCREENING',
      stageAge: 5,
      source: 'Company Website',
      appliedDate: '2024-01-28',
      lastActivity: '2024-02-08',
      skills: ['PMDA Regulations', 'Medical Device Registration', 'Japanese GCP', 'Regulatory Strategy'],
      location: 'Tokyo, Japan',
      currentRole: 'Regulatory Affairs Associate',
      yearsOfExperience: 6,
      assessmentScore: 82,
      interviewScore: 79,
      notes: 'Strong knowledge of Japanese regulatory landscape',
    },
    {
      id: 'CAN-005',
      name: 'Ana Silva',
      email: 'ana.silva@email.com',
      phone: '+55 11 9 8765 4321',
      requisitionId: 'REQ-005',
      requisitionTitle: 'Senior Software Engineer - Digital Health',
      stage: 'INTERVIEW',
      stageAge: 4,
      source: 'Tech Recruiter',
      appliedDate: '2024-01-10',
      lastActivity: '2024-02-09',
      skills: ['React', 'Node.js', 'Healthcare APIs', 'FHIR', 'Cloud Architecture'],
      location: 'São Paulo, Brazil',
      currentRole: 'Software Engineer',
      yearsOfExperience: 7,
      assessmentScore: 88,
      interviewScore: 85,
      notes: 'Excellent healthcare software development experience',
    },
    {
      id: 'CAN-006',
      name: 'Dr. Michael Thompson',
      email: 'michael.thompson@email.com',
      phone: '+1 510 555 0789',
      requisitionId: 'REQ-006',
      requisitionTitle: 'Clinical Operations Director',
      stage: 'INTERVIEW',
      stageAge: 2,
      source: 'Executive Search',
      appliedDate: '2024-01-20',
      lastActivity: '2024-02-12',
      skills: ['Clinical Operations Leadership', 'Budget Management', 'Team Development', 'Process Optimization'],
      location: 'San Francisco, CA',
      currentRole: 'Clinical Operations Manager',
      yearsOfExperience: 15,
      assessmentScore: 91,
      interviewScore: 93,
      notes: 'Strong leadership background in clinical operations',
    }
  ] as Candidate[],

  alerts: [
    {
      id: 'ALERT-001',
      type: 'AGING_CANDIDATE',
      priority: 'HIGH',
      title: 'Aging Candidate Alert',
      description: 'Oliver Schmidt has been in Interview stage for 5 days',
      relatedEntityId: 'CAN-004',
      relatedEntityType: 'CANDIDATE',
      timestamp: '2 hours ago',
      isRead: false,
    },
    {
      id: 'ALERT-002',
      type: 'EXPIRING_OFFER',
      priority: 'HIGH',
      title: 'Offer Expiring Soon',
      description: 'James Martinez offer expires in 3 days',
      relatedEntityId: 'CAN-002',
      relatedEntityType: 'CANDIDATE',
      timestamp: '4 hours ago',
      isRead: false,
    },
    {
      id: 'ALERT-003',
      type: 'SLA_BREACH',
      priority: 'MEDIUM',
      title: 'SLA Breach Warning',
      description: 'REQ-005 DevOps Engineer has exceeded target fill date',
      relatedEntityId: 'REQ-005',
      relatedEntityType: 'REQUISITION',
      timestamp: '1 day ago',
      isRead: false,
    },
    {
      id: 'ALERT-004',
      type: 'NEW_APPLICATION',
      priority: 'LOW',
      title: 'New Applications',
      description: '5 new applications for Senior Software Engineer',
      relatedEntityId: 'REQ-001',
      relatedEntityType: 'REQUISITION',
      timestamp: '1 day ago',
      isRead: true,
    },
    {
      id: 'ALERT-005',
      type: 'INTERVIEW_CONFLICT',
      priority: 'MEDIUM',
      title: 'Interview Scheduling Conflict',
      description: 'Double booking detected for tomorrow 2PM',
      relatedEntityId: 'CAN-001',
      relatedEntityType: 'CANDIDATE',
      timestamp: '2 days ago',
      isRead: true,
    },
  ] as RecruiterAlert[],

  marketIntelligence: {
    talentAvailability: {
      'Medical Device Engineer': { score: 52, trend: 'down' },
      'Clinical Research Manager': { score: 48, trend: 'stable' },
      'Data Scientist - Healthcare': { score: 35, trend: 'down' },
      'Regulatory Affairs Specialist': { score: 67, trend: 'up' },
      'Software Engineer - Digital Health': { score: 58, trend: 'stable' },
      'Clinical Operations Director': { score: 28, trend: 'down' },
      'Quality Assurance Manager': { score: 72, trend: 'up' },
      'Manufacturing Engineer': { score: 65, trend: 'stable' },
      'Biomedical Data Analyst': { score: 41, trend: 'down' },
      'Compliance Officer': { score: 59, trend: 'stable' },
    },
    salaryBenchmarks: {
      'Medical Device Engineer': { min: 85000, median: 105000, max: 135000 },
      'Clinical Research Manager': { min: 95000, median: 120000, max: 150000 },
      'Data Scientist - Healthcare': { min: 100000, median: 125000, max: 160000 },
      'Regulatory Affairs Specialist': { min: 70000, median: 85000, max: 105000 },
      'Software Engineer - Digital Health': { min: 85000, median: 105000, max: 130000 },
      'Clinical Operations Director': { min: 150000, median: 190000, max: 240000 },
      'Quality Assurance Manager': { min: 85000, median: 105000, max: 125000 },
      'Manufacturing Engineer': { min: 70000, median: 85000, max: 105000 },
      'Biomedical Data Analyst': { min: 75000, median: 90000, max: 115000 },
      'Compliance Officer': { min: 80000, median: 100000, max: 125000 },
    },
    competitorActivity: {
      'Medtronic': { hiring: 125, trend: 'increasing' },
      'Johnson & Johnson': { hiring: 95, trend: 'stable' },
      'Abbott': { hiring: 78, trend: 'increasing' },
      'Siemens Healthineers': { hiring: 65, trend: 'stable' },
      'Philips Healthcare': { hiring: 52, trend: 'decreasing' },
      'Baxter International': { hiring: 48, trend: 'stable' },
    },
    sentimentTrends: {
      glassdoor: { rating: 4.1, reviews: 2847, trend: 'positive' },
      linkedin: { mentions: 8945, sentiment: 0.74 },
      indeed: { rating: 3.9, reviews: 1823, trend: 'stable' },
      kununu: { rating: 3.8, reviews: 945, trend: 'improving' },
    },
  },
};